import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';

async function loadSourcePdf(file) {
    const bytes = await file.arrayBuffer();
    try {
        return await PDFDocument.load(bytes, { ignoreEncryption: true });
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('encrypt')) {
            throw new Error('password');
        }
        throw err;
    }
}

/** @returns {Promise<number>} */
export async function getPdfPageCount(file) {
    const pdf = await loadSourcePdf(file);
    return pdf.getPageCount();
}

/**
 * Parse "1-3,5,7" into sorted 0-based page indices.
 * @param {string} text
 * @param {number} totalPages
 * @returns {number[]}
 */
export function parsePageSelection(text, totalPages) {
    const parts = String(text || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (!parts.length) throw new Error('empty');

    const indices = new Set();
    for (const part of parts) {
        if (part.includes('-')) {
            const [rawA, rawB] = part.split('-');
            const a = Number.parseInt(rawA.trim(), 10);
            const b = Number.parseInt(rawB.trim(), 10);
            if (!Number.isFinite(a) || !Number.isFinite(b)) throw new Error('invalid');
            const start = Math.min(a, b);
            const end = Math.max(a, b);
            for (let page = start; page <= end; page++) {
                if (page < 1 || page > totalPages) throw new Error('outOfRange');
                indices.add(page - 1);
            }
        } else {
            const page = Number.parseInt(part, 10);
            if (!Number.isFinite(page) || page < 1 || page > totalPages) throw new Error('outOfRange');
            indices.add(page - 1);
        }
    }
    return [...indices].sort((x, y) => x - y);
}

/**
 * Format 1-based page numbers into a compact selection string (e.g. [1,2,3,5] → "1-3, 5").
 * @param {number[]} pageNumbers 1-based page numbers
 */
export function formatPageSelection(pageNumbers) {
    const sorted = [...new Set(pageNumbers)].filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
    if (!sorted.length) return '';

    const parts = [];
    let rangeStart = sorted[0];
    let rangeEnd = sorted[0];

    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === rangeEnd + 1) {
            rangeEnd = sorted[i];
        } else {
            parts.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);
            rangeStart = sorted[i];
            rangeEnd = sorted[i];
        }
    }

    parts.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);
    return parts.join(', ');
}

async function buildPdfFromIndices(source, indices) {
    const out = await PDFDocument.create();
    const pages = await out.copyPages(source, indices);
    for (const page of pages) out.addPage(page);
    const bytes = await out.save();
    return new Blob([bytes], { type: 'application/pdf' });
}

/**
 * Extract selected pages into one PDF.
 * @param {File} file
 * @param {number[]} indices 0-based page indices
 */
export async function extractPdfPages(file, indices) {
    const source = await loadSourcePdf(file);
    if (!indices.length) throw new Error('empty');
    return buildPdfFromIndices(source, indices);
}

/**
 * Split each page into its own PDF; returns named blobs.
 * @param {File} file
 * @param {(page: number, total: number) => void} [onProgress]
 */
export async function splitPdfEveryPage(file, onProgress) {
    const source = await loadSourcePdf(file);
    const total = source.getPageCount();
    const base = file.name.replace(/\.pdf$/i, '');
    const results = [];

    for (let i = 0; i < total; i++) {
        onProgress?.(i + 1, total);
        const blob = await buildPdfFromIndices(source, [i]);
        results.push({ name: `${base}-page-${i + 1}.pdf`, blob });
    }
    return results;
}

/** @param {{ name: string, blob: Blob }[]} items */
export async function zipPdfFiles(items) {
    const zip = new JSZip();
    for (const item of items) zip.file(item.name, item.blob);
    return zip.generateAsync({ type: 'blob' });
}
